import type { Notification } from '@vlm/api';
import { Button, CountBadge, EmptyState, IconButton, LaneProgress, Popover, PopoverContent, PopoverTrigger, RoadSign, cn } from '@vlm/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Inbox } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ALERT_SIGN } from '../components/status';
import { useT } from '../i18n';
import { alertText, notificationType } from '../lib/alert-text';
import { api } from '../lib/api';
import { useNow, useTimes } from '../lib/time';

const SHOWN = 30;

/** The bell: unread count from the API (refreshed on every live alert), the latest notifications on open. */
export function Notifications() {
  const { t, fmt } = useT();
  const times = useTimes();
  const now = useNow(30_000);
  const client = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const unread = useQuery({ queryKey: ['notifications', 'unread'], queryFn: () => api.notifications.unreadCount(), refetchInterval: 120_000 });
  const list = useQuery({ queryKey: ['notifications', 'list'], queryFn: () => api.notifications.list(), enabled: open });
  const count = unread.data?.count ?? 0;

  const markRead = useMutation({
    mutationFn: (ids: number[]) => Promise.all(ids.map((id) => api.notifications.markRead(id))),
    onSettled: () => client.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const items = (list.data ?? []).slice(0, SHOWN);
  const unreadShown = items.filter((n) => n.status === 'UNREAD').map((n) => n.notificationId);

  const openAlert = (n: Notification) => {
    if (n.status === 'UNREAD') markRead.mutate([n.notificationId]);
    setOpen(false);
    navigate(`/alerts?alert=${n.alertId}`);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <IconButton
          label={count > 0 ? t('notifications.unreadLabel', { count }) : t('notifications.open')}
          icon={<Inbox size={20} aria-hidden="true" />}
          badge={<CountBadge count={count} />}
          tooltip={!open}
        />
      </PopoverTrigger>
      <PopoverContent className="grid w-[min(400px,calc(100vw-24px))] grid-rows-[auto_minmax(0,1fr)_auto]">
        <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
          <div className="flex items-baseline gap-2">
            <h2 className="text-title text-ink">{t('notifications.title')}</h2>
            {count > 0 && <span className="text-body-sm text-ink-subtle">{t('notifications.unread', { count })}</span>}
          </div>
          {unreadShown.length > 0 && (
            <Button variant="ghost" size="sm" loading={markRead.isPending} onClick={() => markRead.mutate(unreadShown)}>
              {t('notifications.markAllRead')}
            </Button>
          )}
        </div>
        <div className="relative max-h-[min(460px,60vh)] min-h-0 overflow-y-auto">
          {list.isFetching && <LaneProgress thin label={t('common.loading')} className="absolute inset-x-0 top-0" />}
          {list.isSuccess && items.length === 0 && <EmptyState title={t('notifications.empty')} body={t('notifications.emptyBody')} />}
          <ul>
            {items.map((n) => {
              const type = notificationType(n);
              const fresh = n.status === 'UNREAD';
              return (
                <li key={n.notificationId}>
                  <button
                    type="button"
                    onClick={() => openAlert(n)}
                    className={cn(
                      'grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-3 border-b border-line px-4 py-3 text-start transition-colors duration-quick hover:bg-hover',
                      fresh && 'bg-selected/40',
                    )}
                  >
                    <RoadSign sign={type ? ALERT_SIGN[type] : 'info'} size={20} className="mt-0.5" />
                    <span className="min-w-0">
                      <span className={cn('block truncate text-body-sm text-ink', fresh ? 'font-bold' : 'font-semibold')}>{type ? t(`alertTypes.${type}`) : n.title}</span>
                      <span className="line-clamp-2 text-body-sm text-ink-muted">{(type && alertText(type, n.params, t, fmt)) ?? n.message}</span>
                    </span>
                    <span className="whitespace-nowrap font-mono text-[12px] text-ink-subtle">{times.ago(n.createdTime, now)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="border-t border-line px-4 py-2.5">
          <Link to="/alerts" onClick={() => setOpen(false)} className="text-body-sm font-bold">
            {t('notifications.viewAlerts')}
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
